import { GameModes } from "@Enums/index";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import { type IPlayItemResult } from "@Globals/types";
import { getPerformanceOutcome, type PerformanceOutcome } from "@Helpers/feedback";

import { type IPerformanceStats, type IScenarioData, type RawSessionResultsData, type SessionResultsData } from "./use-session-results.types";

const METRICS: MetricFilterTypes[] = [
  MetricFilterTypes.BYB_IQ,
  MetricFilterTypes.SCAN_FREQUENCY,
  MetricFilterTypes.SCAN_TIMING,
  MetricFilterTypes.CRITICAL_SCAN,
  MetricFilterTypes.DECISION,
  MetricFilterTypes.AWARENESS,
];

const isAwarenessTestInPlayItem = (playItem: IPlayItemResult) => playItem?.labels_made !== -1;

const getSummaryTabMetricScore = (metric: MetricFilterTypes, sessionResults: SessionResultsData): number => {
  if (metric === MetricFilterTypes.BYB_IQ) {
    return sessionResults.byb_iq;
  }
  return sessionResults.scores?.[metric]?.final_score ?? 0;
};

export const getSessionFeedbackReplayResultId = (sessionResults: SessionResultsData | null | undefined, currentTab: number): number => {
  if (!sessionResults || currentTab <= 0) {
    return 0;
  }

  const result = sessionResults.play_item_results?.[currentTab - 1];
  const item = sessionResults.play_items?.[currentTab - 1];

  for (const value of [result?.cognitive_result_id, result?.id, item?.cognitive_result_id, item?.id]) {
    const id = Number(value);
    if (id > 0) {
      return id;
    }
  }

  return 0;
};

export interface ReplayScenarioTarget {
  tab: number;
  cognitiveResultId: number;
}

export const buildReplayScenarioTargets = (sessionResults: SessionResultsData | null | undefined): ReplayScenarioTarget[] => {
  const scenarioCount =
    !sessionResults ? 0 : sessionResults.game_mode === GameModes.MATCH_PLAY ? 1 : sessionResults.play_item_results.length;

  return Array.from({ length: scenarioCount }, (_, index) => ({
    tab: index + 1,
    cognitiveResultId: getSessionFeedbackReplayResultId(sessionResults, index + 1),
  })).filter(target => target.cognitiveResultId > 0);
};

export const normalizeSessionResults = (response: RawSessionResultsData): SessionResultsData => {
  const normalizedSessionScore = {
    final_score: response.session_score?.final_score ?? 0,
    target_score: response.session_score?.target_score ?? 0,
  };

  return {
    failed: Boolean(response.failed),
    cognitive_result_id: response.cognitive_result_id ?? 0,
    byb_iq: response.byb_iq ?? 0,
    play_items: response.play_items ?? [],
    play_item_results: response.play_item_results ?? [],
    score_goals: response.score_goals ?? {},
    scores: response.scores ?? {},
    session_score: normalizedSessionScore,
    previous_score: response.previous_score ?? response.previous_session_results?.session_score?.final_score ?? 0,
    created_at: response.created_at ?? response.creation_date ?? "",
    game_mode: response.game_mode,
    session_type: response.session_type,
  };
};

export const deriveScenarioData = (
  sessionResults: SessionResultsData | null,
  currentTab: number
): {
  scenarioData: IScenarioData | null;
  targetOutcome: PerformanceOutcome | null;
} => {
  if (!sessionResults) {
    return {
      scenarioData: null,
      targetOutcome: null,
    };
  }

  const playItemResults = sessionResults.play_item_results;
  const playItem = currentTab === 0 ? null : playItemResults[currentTab - 1];

  const getScenarioTabMetricScore = (playItem: IPlayItemResult, metric: MetricFilterTypes): number => {
    if (metric === MetricFilterTypes.BYB_IQ) {
      return playItem.byb_iq;
    }
    return playItem.scores?.[metric]?.final_score ?? 0;
  };

  // TODO: Redo this mapping once the session results API exposes a unified metric shape for summary vs per-scenario
  // (single source for score/target per metric tab), so we can drop branching across session_score, scores,
  // score_goals, play_item fields, and awareness edge cases here.
  const performanceStats = METRICS.map(metric => {
    const targetFromGoals = sessionResults.score_goals[metric];
    const hasAwarenessInSession = playItemResults.some(isAwarenessTestInPlayItem);

    // `score_goals` lists every tracked metric except BYB IQ (BYB IQ targets use `session_score` only).
    if (metric !== MetricFilterTypes.BYB_IQ && !targetFromGoals) {
      return null;
    }

    // Overall (summary) tab: hide Awareness entirely when this session had no awareness test at all.
    if (metric === MetricFilterTypes.AWARENESS && !hasAwarenessInSession) {
      return null;
    }

    // Scenario tabs: hide Awareness for scenarios that did not run an awareness test (`labels_made === -1`).
    if (currentTab > 0 && metric === MetricFilterTypes.AWARENESS && playItem && !isAwarenessTestInPlayItem(playItem)) {
      return null;
    }

    const summaryScore = getSummaryTabMetricScore(metric, sessionResults);
    const scenarioScore = playItem ? getScenarioTabMetricScore(playItem, metric) : 0;
    const score = currentTab === 0 ? summaryScore : scenarioScore;
    const progress = currentTab === 0 ? summaryScore : scenarioScore;
    const target = metric === MetricFilterTypes.BYB_IQ ? sessionResults.session_score.target_score : (targetFromGoals?.target_score ?? 0);

    return {
      type: metric,
      score,
      progress,
      target,
    } satisfies IPerformanceStats;
  }).filter((stat): stat is IPerformanceStats => Boolean(stat));

  const cognitiveResultId = currentTab === 0 ? sessionResults.cognitive_result_id : getSessionFeedbackReplayResultId(sessionResults, currentTab);
  const outcomeScore = currentTab === 0 ? sessionResults.session_score.final_score : (playItem?.total_score ?? 0);
  const targetScore = sessionResults.session_score.target_score;

  return {
    scenarioData: {
      cognitive_result_id: cognitiveResultId,
      performanceStats,
    },
    targetOutcome: getPerformanceOutcome(outcomeScore, targetScore),
  };
};
